import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, 
  Plus, 
  Trash2,
  Edit3,
  Mail,
  Phone,
  User,
  Briefcase,
  Heart,
  Stethoscope,
  Scale,
  Shield,
  ArrowUpDown,
  FileDown,
  Loader2,
  Lock,
  Save,
  X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { useLifely } from '../context/LifelyContext';
import { CONTACT_CATEGORIES, CONTACT_ROLES } from '../store/mockData';
import { toast } from 'sonner';

const SORT_OPTIONS = [
  { value: 'name-asc', label: 'Name A → Z' },
  { value: 'name-desc', label: 'Name Z → A' },
  { value: 'category', label: 'Category' },
  { value: 'date-newest', label: 'Date (Newest)' },
  { value: 'date-oldest', label: 'Date (Oldest)' },
];

// Comprehensive list of country codes (alphabetically by country name)
const COUNTRY_CODES = [
  { code: '+93', country: 'Afghanistan' },
  { code: '+355', country: 'Albania' },
  { code: '+213', country: 'Algeria' },
  { code: '+376', country: 'Andorra' },
  { code: '+244', country: 'Angola' },
  { code: '+54', country: 'Argentina' },
  { code: '+374', country: 'Armenia' },
  { code: '+61', country: 'Australia' },
  { code: '+43', country: 'Austria' },
  { code: '+994', country: 'Azerbaijan' },
  { code: '+973', country: 'Bahrain' },
  { code: '+880', country: 'Bangladesh' },
  { code: '+375', country: 'Belarus' },
  { code: '+32', country: 'Belgium' },
  { code: '+501', country: 'Belize' },
  { code: '+229', country: 'Benin' },
  { code: '+975', country: 'Bhutan' },
  { code: '+591', country: 'Bolivia' },
  { code: '+387', country: 'Bosnia' },
  { code: '+267', country: 'Botswana' },
  { code: '+55', country: 'Brazil' },
  { code: '+673', country: 'Brunei' },
  { code: '+359', country: 'Bulgaria' },
  { code: '+855', country: 'Cambodia' },
  { code: '+237', country: 'Cameroon' },
  { code: '+1', country: 'Canada/USA' },
  { code: '+56', country: 'Chile' },
  { code: '+86', country: 'China' },
  { code: '+57', country: 'Colombia' },
  { code: '+506', country: 'Costa Rica' },
  { code: '+385', country: 'Croatia' },
  { code: '+53', country: 'Cuba' },
  { code: '+357', country: 'Cyprus' },
  { code: '+420', country: 'Czech Republic' },
  { code: '+45', country: 'Denmark' },
  { code: '+593', country: 'Ecuador' },
  { code: '+20', country: 'Egypt' },
  { code: '+503', country: 'El Salvador' },
  { code: '+372', country: 'Estonia' },
  { code: '+251', country: 'Ethiopia' },
  { code: '+679', country: 'Fiji' },
  { code: '+358', country: 'Finland' },
  { code: '+33', country: 'France' },
  { code: '+995', country: 'Georgia' },
  { code: '+49', country: 'Germany' },
  { code: '+233', country: 'Ghana' },
  { code: '+30', country: 'Greece' },
  { code: '+502', country: 'Guatemala' },
  { code: '+504', country: 'Honduras' },
  { code: '+852', country: 'Hong Kong' },
  { code: '+36', country: 'Hungary' },
  { code: '+354', country: 'Iceland' },
  { code: '+91', country: 'India' },
  { code: '+62', country: 'Indonesia' },
  { code: '+98', country: 'Iran' },
  { code: '+964', country: 'Iraq' },
  { code: '+353', country: 'Ireland' },
  { code: '+972', country: 'Israel' },
  { code: '+39', country: 'Italy' },
  { code: '+81', country: 'Japan' },
  { code: '+962', country: 'Jordan' },
  { code: '+7', country: 'Kazakhstan' },
  { code: '+254', country: 'Kenya' },
  { code: '+82', country: 'South Korea' },
  { code: '+965', country: 'Kuwait' },
  { code: '+996', country: 'Kyrgyzstan' },
  { code: '+856', country: 'Laos' },
  { code: '+371', country: 'Latvia' },
  { code: '+961', country: 'Lebanon' },
  { code: '+218', country: 'Libya' },
  { code: '+423', country: 'Liechtenstein' },
  { code: '+370', country: 'Lithuania' },
  { code: '+352', country: 'Luxembourg' },
  { code: '+853', country: 'Macau' },
  { code: '+60', country: 'Malaysia' },
  { code: '+960', country: 'Maldives' },
  { code: '+356', country: 'Malta' },
  { code: '+52', country: 'Mexico' },
  { code: '+373', country: 'Moldova' },
  { code: '+377', country: 'Monaco' },
  { code: '+976', country: 'Mongolia' },
  { code: '+382', country: 'Montenegro' },
  { code: '+212', country: 'Morocco' },
  { code: '+258', country: 'Mozambique' },
  { code: '+95', country: 'Myanmar' },
  { code: '+264', country: 'Namibia' },
  { code: '+977', country: 'Nepal' },
  { code: '+31', country: 'Netherlands' },
  { code: '+64', country: 'New Zealand' },
  { code: '+505', country: 'Nicaragua' },
  { code: '+234', country: 'Nigeria' },
  { code: '+389', country: 'North Macedonia' },
  { code: '+47', country: 'Norway' },
  { code: '+968', country: 'Oman' },
  { code: '+92', country: 'Pakistan' },
  { code: '+507', country: 'Panama' },
  { code: '+595', country: 'Paraguay' },
  { code: '+51', country: 'Peru' },
  { code: '+63', country: 'Philippines' },
  { code: '+48', country: 'Poland' },
  { code: '+351', country: 'Portugal' },
  { code: '+974', country: 'Qatar' },
  { code: '+40', country: 'Romania' },
  { code: '+7', country: 'Russia' },
  { code: '+250', country: 'Rwanda' },
  { code: '+966', country: 'Saudi Arabia' },
  { code: '+381', country: 'Serbia' },
  { code: '+65', country: 'Singapore' },
  { code: '+421', country: 'Slovakia' },
  { code: '+386', country: 'Slovenia' },
  { code: '+27', country: 'South Africa' },
  { code: '+34', country: 'Spain' },
  { code: '+94', country: 'Sri Lanka' },
  { code: '+46', country: 'Sweden' },
  { code: '+41', country: 'Switzerland' },
  { code: '+886', country: 'Taiwan' },
  { code: '+992', country: 'Tajikistan' },
  { code: '+255', country: 'Tanzania' },
  { code: '+66', country: 'Thailand' },
  { code: '+216', country: 'Tunisia' },
  { code: '+90', country: 'Turkey' },
  { code: '+993', country: 'Turkmenistan' },
  { code: '+256', country: 'Uganda' },
  { code: '+380', country: 'Ukraine' },
  { code: '+971', country: 'UAE' },
  { code: '+44', country: 'United Kingdom' },
  { code: '+598', country: 'Uruguay' },
  { code: '+998', country: 'Uzbekistan' },
  { code: '+58', country: 'Venezuela' },
  { code: '+84', country: 'Vietnam' },
  { code: '+967', country: 'Yemen' },
  { code: '+260', country: 'Zambia' },
  { code: '+263', country: 'Zimbabwe' },
];

const getCategoryIcon = (category) => {
  switch (category) {
    case 'trusted': return Shield;
    case 'family': return Heart;
    case 'legal': return Scale;
    case 'financial': return Briefcase;
    case 'medical': return Stethoscope;
    case 'professional': return Briefcase;
    default: return User;
  }
};

const getCategoryColor = (category) => {
  switch (category) {
    case 'trusted': return 'bg-amber-500/10 text-amber-600 border-amber-500/20';
    case 'family': return 'bg-pink-500/10 text-pink-600 border-pink-500/20';
    case 'legal': return 'bg-blue-500/10 text-blue-600 border-blue-500/20';
    case 'financial': return 'bg-green-500/10 text-green-600 border-green-500/20';
    case 'medical': return 'bg-red-500/10 text-red-600 border-red-500/20';
    case 'professional': return 'bg-purple-500/10 text-purple-600 border-purple-500/20';
    default: return 'bg-muted text-muted-foreground border-border';
  }
};

// My Contact Details Card Component
const MyContactDetailsCard = ({ user, userContactPrefs, updateUserContactPrefs, countryCodes }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [altEmail, setAltEmail] = useState(userContactPrefs.alternativeEmail || '');
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [useAltEmail, setUseAltEmail] = useState(userContactPrefs.useAlternativeEmail || false);
  const [emailError, setEmailError] = useState('');

  // Parse existing phone on mount
  useEffect(() => {
    const existingPhone = userContactPrefs.phone || '';
    if (existingPhone) {
      const matchedCode = countryCodes.find(c => existingPhone.startsWith(c.code));
      if (matchedCode) {
        setCountryCode(matchedCode.code);
        setPhone(existingPhone.replace(matchedCode.code, '').trim());
      } else {
        setPhone(existingPhone);
      }
    }
  }, [userContactPrefs.phone, countryCodes]);

  const validateEmail = (email) => {
    if (!email) return '';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) ? '' : 'Invalid email format';
  };

  const handleSave = () => {
    if (altEmail && validateEmail(altEmail)) {
      setEmailError(validateEmail(altEmail));
      return;
    }
    
    const fullPhone = phone.trim() ? (countryCode ? `${countryCode} ${phone.trim()}` : phone.trim()) : '';
    
    updateUserContactPrefs({
      alternativeEmail: altEmail.trim(),
      phone: fullPhone,
      useAlternativeEmail: useAltEmail,
    });
    
    setIsEditing(false);
    toast.success('Contact details updated');
  };

  const handleCancel = () => {
    setAltEmail(userContactPrefs.alternativeEmail || '');
    setUseAltEmail(userContactPrefs.useAlternativeEmail || false);
    const existingPhone = userContactPrefs.phone || '';
    const matchedCode = countryCodes.find(c => existingPhone.startsWith(c.code));
    if (matchedCode) {
      setCountryCode(matchedCode.code);
      setPhone(existingPhone.replace(matchedCode.code, '').trim());
    } else {
      setCountryCode('');
      setPhone(existingPhone);
    }
    setEmailError('');
    setIsEditing(false);
  };

  if (!user) return null;

  return (
    <Card className="border-2 border-primary/30 bg-primary/5" data-testid="my-contact-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base font-medium">
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
              <User className="w-4 h-4 text-primary" />
            </div>
            My Contact Details
          </CardTitle>
          {!isEditing ? (
            <Button variant="ghost" size="sm" onClick={() => setIsEditing(true)}>
              <Edit3 className="w-4 h-4 mr-1" /> Edit
            </Button>
          ) : (
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" onClick={handleCancel}>
                <X className="w-4 h-4" />
              </Button>
              <Button variant="default" size="sm" onClick={handleSave}>
                <Save className="w-4 h-4 mr-1" /> Save
              </Button>
            </div>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Used for will reminders, continuity check-ins, and account communications
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Locked Clerk-managed fields */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground flex items-center gap-1">
              <Lock className="w-3 h-3" /> Name
            </Label>
            <p className="text-sm font-medium">
              {user.firstName || ''} {user.lastName || ''}
            </p>
          </div>
          <div className="space-y-1 col-span-2 sm:col-span-2">
            <Label className="text-xs text-muted-foreground flex items-center gap-1">
              <Lock className="w-3 h-3" /> Primary Email
            </Label>
            <p className="text-sm font-medium">{user.email || '-'}</p>
          </div>
        </div>

        {/* Editable fields */}
        <div className="pt-3 border-t border-border space-y-3">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              {isEditing ? (
                <>
                  <Checkbox 
                    id="use-alt-email" 
                    checked={useAltEmail} 
                    onCheckedChange={setUseAltEmail}
                  />
                  <Label htmlFor="use-alt-email" className="text-sm font-normal cursor-pointer">
                    Use alternative email for communications
                  </Label>
                </>
              ) : useAltEmail && altEmail ? (
                <Badge variant="outline" className="text-xs">Using alternative email</Badge>
              ) : null}
            </div>
            
            {isEditing && (
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Alternative Email</Label>
                <Input 
                  type="email"
                  value={altEmail}
                  onChange={(e) => {
                    setAltEmail(e.target.value);
                    setEmailError(validateEmail(e.target.value));
                  }}
                  placeholder="alternative@email.com"
                  className={`h-9 ${emailError ? 'border-destructive' : ''}`}
                />
                {emailError && <p className="text-xs text-destructive">{emailError}</p>}
              </div>
            )}
            
            {!isEditing && userContactPrefs.alternativeEmail && (
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Alternative Email</Label>
                <p className="text-sm">{userContactPrefs.alternativeEmail}</p>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Phone Number</Label>
            {isEditing ? (
              <>
                <div className="flex gap-2">
                  <Select value={countryCode} onValueChange={setCountryCode}>
                    <SelectTrigger className="h-9 w-40">
                      <SelectValue placeholder="Country" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {countryCodes.map(c => (
                        <SelectItem key={`${c.code}-${c.country}`} value={c.code}>
                          {c.country} ({c.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input 
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Phone number"
                    className="h-9 flex-1"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Required for SMS check-ins (Plus plan)
                </p>
              </>
            ) : (
              <p className="text-sm">{userContactPrefs.phone || <span className="text-muted-foreground italic">Not set</span>}</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const Contacts = () => {
  const { 
    contacts, 
    addContact, 
    updateContact, 
    removeContact, 
    fetchContacts, 
    loading, 
    isAuthenticated,
    user,
    userContactPrefs,
    updateUserContactPrefs
  } = useLifely();
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [sortBy, setSortBy] = useState('name-asc');
  
  // Fetch contacts when component mounts
  useEffect(() => {
    if (isAuthenticated) {
      fetchContacts();
    }
  }, [isAuthenticated, fetchContacts]);
  
  // Form state
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [category, setCategory] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [notes, setNotes] = useState('');

  // Email validation
  const validateEmail = (email) => {
    if (!email) return '';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) ? '' : 'Please enter a valid email address';
  };

  // Phone validation (basic - just numbers after country code)
  const validatePhone = (phone) => {
    if (!phone) return '';
    const phoneRegex = /^[\d\s-]+$/;
    return phoneRegex.test(phone) ? '' : 'Please enter a valid phone number (digits only)';
  };

  const resetForm = () => {
    setName('');
    setRole('');
    setCategory('');
    setEmail('');
    setEmailError('');
    setPhone('');
    setPhoneError('');
    setCountryCode('');
    setNotes('');
  };

  const handleOpenDialog = (contact = null) => {
    if (contact) {
      setEditingContact(contact);
      setName(contact.name || '');
      setRole(contact.role || '');
      setCategory(contact.category || '');
      setEmail(contact.email || '');
      // Parse existing phone to extract country code
      const existingPhone = contact.phone || '';
      const matchedCode = COUNTRY_CODES.find(c => existingPhone.startsWith(c.code));
      if (matchedCode) {
        setCountryCode(matchedCode.code);
        setPhone(existingPhone.replace(matchedCode.code, '').trim());
      } else {
        setCountryCode('');
        setPhone(existingPhone);
      }
      setNotes(contact.notes || '');
    } else {
      setEditingContact(null);
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!category) {
      toast.error('Category is required');
      return;
    }

    // Validate email
    const emailErr = validateEmail(email.trim());
    if (emailErr) {
      setEmailError(emailErr);
      toast.error(emailErr);
      return;
    }

    // Validate phone
    const phoneErr = validatePhone(phone.trim());
    if (phoneErr) {
      setPhoneError(phoneErr);
      toast.error(phoneErr);
      return;
    }

    // Format phone with country code (only if both are present)
    let fullPhone = '';
    if (phone.trim()) {
      fullPhone = countryCode ? `${countryCode} ${phone.trim()}` : phone.trim();
    }

    const contactData = {
      name: name.trim(),
      role,
      category,
      email: email.trim(),
      phone: fullPhone,
      notes: notes.trim(),
    };

    try {
      if (editingContact) {
        await updateContact(editingContact.id, contactData);
        toast.success('Contact updated');
      } else {
        await addContact(contactData);
        toast.success('Contact added');
      }
      
      setIsDialogOpen(false);
      resetForm();
    } catch (err) {
      toast.error('Failed to save contact');
    }
  };

  const handleDelete = async (contactId) => {
    try {
      await removeContact(contactId);
      toast.success('Contact removed');
    } catch (err) {
      toast.error('Failed to remove contact');
    }
  };

  // Filter contacts
  const filteredContacts = filterCategory === 'all' 
    ? contacts 
    : contacts.filter(c => c.category === filterCategory);

  // Sort contacts
  const sortedContacts = useMemo(() => {
    const sorted = [...filteredContacts];
    switch (sortBy) {
      case 'name-asc':
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
      case 'name-desc':
        return sorted.sort((a, b) => b.name.localeCompare(a.name));
      case 'category':
        return sorted.sort((a, b) => (a.category || '').localeCompare(b.category || ''));
      case 'date-newest':
        return sorted.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
      case 'date-oldest':
        return sorted.sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''));
      default:
        return sorted;
    }
  }, [filteredContacts, sortBy]);

  // Group contacts by category
  const groupedContacts = sortedContacts.reduce((acc, contact) => {
    const cat = contact.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(contact);
    return acc;
  }, {});

  const getRoleLabel = (roleValue) => {
    const roleObj = CONTACT_ROLES.find(r => r.value === roleValue);
    return roleObj ? roleObj.label : roleValue;
  };

  const getCategoryLabel = (catValue) => {
    const catObj = CONTACT_CATEGORIES.find(c => c.value === catValue);
    return catObj ? catObj.label : catValue;
  };

  // Get roles filtered by selected category
  const filteredRoles = category 
    ? CONTACT_ROLES.filter(r => r.category === category)
    : CONTACT_ROLES;

  // Show loading state
  if (loading.contacts) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="contacts-page">
      {/* Header */}
      <section className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-foreground">
            Important Contacts
          </h1>
          <p className="text-sm text-muted-foreground max-w-xl">
            Manage key contacts that can be referenced throughout Lifely. Add family members, legal advisors, financial professionals, and others.
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-contact-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Contact
        </Button>
      </section>

      {/* My Contact Details Card */}
      {user && (
        <MyContactDetailsCard 
          user={user}
          userContactPrefs={userContactPrefs}
          updateUserContactPrefs={updateUserContactPrefs}
          countryCodes={COUNTRY_CODES}
        />
      )}

      {/* Filter & Sort */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">Filter:</Label>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-40 h-8" data-testid="filter-category-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {CONTACT_CATEGORIES.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex items-center gap-2">
          <ArrowUpDown className="w-3.5 h-3.5 text-muted-foreground" />
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-36 h-8" data-testid="sort-contacts-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <span className="text-xs text-muted-foreground">
          {sortedContacts.length} contact{sortedContacts.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Contacts Grid */}
      {sortedContacts.length === 0 ? (
        <Card className="border border-dashed border-border">
          <CardContent className="py-12 text-center">
            <Users className="w-10 h-10 mx-auto text-muted-foreground/50 mb-3" />
            <p className="text-muted-foreground">No contacts yet</p>
            <p className="text-sm text-muted-foreground mt-1">
              Add your first contact to get started.
            </p>
            <Button onClick={() => handleOpenDialog()} variant="outline" className="mt-4">
              <Plus className="w-4 h-4 mr-2" />
              Add Contact
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {CONTACT_CATEGORIES.map(cat => {
            const catContacts = groupedContacts[cat.value];
            if (!catContacts || catContacts.length === 0) return null;
            
            const Icon = getCategoryIcon(cat.value);
            
            return (
              <div key={cat.value} className="space-y-3">
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-muted-foreground" />
                  <h2 className="text-sm font-medium text-foreground">{cat.label}</h2>
                  <span className="text-xs text-muted-foreground">({catContacts.length})</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {catContacts.map(contact => (
                    <Card key={contact.id} className="border border-border hover:border-primary/30 transition-colors" data-testid={`contact-card-${contact.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-sm font-medium truncate">{contact.name}</h3>
                            </div>
                            {contact.role && (
                              <Badge variant="outline" className={`text-xs mb-2 ${getCategoryColor(contact.category)}`}>
                                {getRoleLabel(contact.role)}
                              </Badge>
                            )}
                            <div className="space-y-1">
                              {contact.email && (
                                <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                                  <Mail className="w-3 h-3" />
                                  <span className="truncate">{contact.email}</span>
                                </p>
                              )}
                              {contact.phone && (
                                <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                                  <Phone className="w-3 h-3" />
                                  {contact.phone}
                                </p>
                              )}
                            </div>
                            {contact.notes && (
                              <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{contact.notes}</p>
                            )}
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <div className="flex items-center gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-8 w-8 p-0"
                                onClick={() => handleOpenDialog(contact)}
                                data-testid={`edit-contact-${contact.id}`}
                              >
                                <Edit3 className="w-3.5 h-3.5" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                onClick={() => handleDelete(contact.id)}
                                data-testid={`delete-contact-${contact.id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                            <Link to={`/contacts/${contact.id}/export`}>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-7 px-2 text-xs text-muted-foreground hover:text-primary"
                                data-testid={`export-contact-${contact.id}`}
                              >
                                <FileDown className="w-3 h-3 mr-1" />
                                Export for
                              </Button>
                            </Link>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-heading">
              {editingContact ? 'Edit Contact' : 'Add Contact'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Name *</Label>
              <Input 
                value={name} 
                onChange={(e) => setName(e.target.value)} 
                placeholder="Full name or organization"
                className="h-9"
                data-testid="contact-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Category *</Label>
                <Select value={category} onValueChange={(v) => { setCategory(v); setRole(''); }}>
                  <SelectTrigger className="h-9" data-testid="contact-category-select">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTACT_CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Role / Relationship</Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger className="h-9" data-testid="contact-role-select">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredRoles.map(r => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Email</Label>
              <Input 
                type="email"
                value={email} 
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailError(validateEmail(e.target.value));
                }} 
                placeholder="email@example.com"
                className={`h-9 ${emailError ? 'border-destructive' : ''}`}
              />
              {emailError && <p className="text-xs text-destructive">{emailError}</p>}
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Phone</Label>
              <div className="flex gap-2">
                <Select value={countryCode} onValueChange={setCountryCode}>
                  <SelectTrigger className="h-9 w-40">
                    <SelectValue placeholder="Country" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {COUNTRY_CODES.map(c => (
                      <SelectItem key={`${c.code}-${c.country}`} value={c.code}>
                        {c.country} ({c.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input 
                  type="tel"
                  value={phone} 
                  onChange={(e) => {
                    setPhone(e.target.value);
                    setPhoneError(validatePhone(e.target.value));
                  }} 
                  placeholder="Phone number"
                  className={`h-9 flex-1 ${phoneError ? 'border-destructive' : ''}`}
                />
              </div>
              {phoneError && <p className="text-xs text-destructive">{phoneError}</p>}
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Notes</Label>
              <Textarea 
                value={notes} 
                onChange={(e) => setNotes(e.target.value)} 
                placeholder="Any additional information..."
                className="min-h-[60px] resize-none text-sm"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} data-testid="save-contact-btn">
              {editingContact ? 'Save Changes' : 'Add Contact'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Info Note */}
      <div className="bg-muted/50 rounded-lg p-4 border border-border">
        <p className="text-xs text-muted-foreground">
          Contacts added here can be selected when filling in forms throughout Lifely, such as executors, advisors, and emergency contacts.
        </p>
      </div>
    </div>
  );
};

export default Contacts;
